import axios from 'axios'

const apiClient = axios.create({
  baseURL: '',
  withCredentials: true,
  timeout: 15_000,
})

// Attach _suppressRedirect: true to a request to skip the automatic
// 401 login-redirect for that call (e.g. "am I logged in?" probes).
declare module 'axios' {
  interface AxiosRequestConfig {
    _suppressRedirect?: boolean
  }
}

// ── German decimal input (0,00) ───────────────────────────────────────────────
// In Germany numbers use a comma decimal separator (0,00 / 1.234,56). The backend
// (and PostgreSQL numeric) expect a dot (0.00 / 1234.56). Rather than fixing every
// form, we normalize here centrally: any request value that is *purely* a German-
// formatted number string gets converted on the way out. Free text is never
// touched — the whole string must match a number-with-comma pattern.
const DE_THOUSANDS = /^-?\d{1,3}(\.\d{3})+,\d+$/ // 1.234,56
const DE_DECIMAL = /^-?\d+,\d+$/                  // 25,49 / 0,00

function normalizeGermanNumberString(s: string): string {
  if (DE_THOUSANDS.test(s)) return s.replace(/\./g, '').replace(',', '.')
  if (DE_DECIMAL.test(s)) return s.replace(',', '.')
  return s
}

function normalizeDeep(value: unknown): unknown {
  if (typeof value === 'string') return normalizeGermanNumberString(value)
  if (Array.isArray(value)) return value.map(normalizeDeep)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = normalizeDeep(v)
    return out
  }
  return value
}

apiClient.interceptors.request.use((config) => {
  const m = (config.method || 'get').toLowerCase()
  // Only mutate plain-object JSON bodies; never FormData / strings / Blobs.
  if (
    (m === 'post' || m === 'put' || m === 'patch') &&
    config.data &&
    typeof config.data === 'object' &&
    !(config.data instanceof FormData) &&
    !(config.data instanceof Blob) &&
    !(config.data instanceof ArrayBuffer)
  ) {
    config.data = normalizeDeep(config.data)
  }
  return config
})

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?._suppressRedirect) {
      // Session expired or cookie missing — redirect to the appropriate
      // login page. Full reload resets the in-memory Zustand store so
      // the user sees the login form rather than stale cached data.
      // Host-based detection (MUST match isAdminHost() in App.tsx): admin is
      // the control.* subdomain or the legacy :3443 port → /login. Everything
      // else (zkm.*, portal, public shop) → /portal/login. A mismatch here
      // causes an infinite redirect/reload loop: the router bounces the
      // unknown path (/portal/login on an admin host) back to "/", which
      // re-fires the 401, which redirects again.
      const isAdmin =
        window.location.port === '3443' ||
        window.location.hostname.startsWith('control.')
      const target = isAdmin ? '/login' : '/portal/login'
      if (window.location.pathname !== target) {
        const from = window.location.pathname + window.location.search
        window.location.href = `${target}?redirect=${encodeURIComponent(from)}`
      }
    }
    return Promise.reject(error)
  },
)

export default apiClient
